import { DriverOccupation, PcDriver } from '@acorn-portal/shared/src/services/subscriptionAPI';
import { DriverShortFormValues } from '@acorn-portal/shared/src/utils/initialValues/get-driver-short-form-values';

export const prepareOccupation = (
	occupation: string,
	businessType: string,
	employmentType: string,
	isFullTime: string,
): DriverOccupation => ({
	occupation,
	businessType,
	employmentStatus: employmentType, // confusing mapping on the backend, it's the code for employed, self employed etc; e.g. 448
	isPartTime: isFullTime === 'no',
});

/**
 * Generates a new array of driver data by updating the specified driver's occupations
 * based on the provided edit form values.
 *
 * This function is typically used to update working driver information in Redux state,
 * after a user edits driver details in the DriverShortForm.
 *
 * @param formValues - The values from the DriverShortForm.
 * @param driverIndex - The index of the driver to update in the driver data array
 * @param storeData - The current array of driver data to be updated.
 * @returns A new array of driver data with the updated driver occupations.
 **/
export const updateDriversFromShortForm = (
	formValues: DriverShortFormValues,
	driverIndex: number,
	storeData: PcDriver[],
): PcDriver[] => {
	// Clone data for immutability
	const clonedDrivers = structuredClone(storeData);
	const updatedDriver = { ...clonedDrivers[driverIndex] };
	const updatedOccupations = [];

	const mainOccupation = prepareOccupation(
		formValues.occupation, // code for the job itself; e.g. 448
		formValues.industry,
		formValues.employmentType, // code for employed, self employed etc; e.g. E
		formValues.employmentStatus, // yes/no for full time
	);
	updatedOccupations[0] = mainOccupation;

	if (formValues.otherEmployment === 'yes') {
		const otherOccupation = prepareOccupation(
			formValues.otherOccupation, // code for the job itself; e.g. 448
			formValues.otherIndustry,
			formValues.otherEmploymentType, // code for employed, self employed etc; e.g. E
			formValues.otherEmploymentStatus, // yes/no for full time
		);
		updatedOccupations[1] = otherOccupation;
	}

	updatedDriver.driverOccupations = updatedOccupations;
	clonedDrivers[driverIndex] = updatedDriver;

	return clonedDrivers;
};
